import { mergeAttributes } from '@tiptap/core';
import type { Node as ProsemirrorNode } from '@tiptap/pm/model';
import { Fragment, Slice } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Node_Type } from 'ricos-schema';
import paragraphDataDefaults from 'ricos-schema/dist/statics/paragraph.defaults.json';
import type { DOMOutputSpec } from 'ricos-types';
import { createRicosNodeExtension } from 'wix-tiptap-extensions';

import {
  createDirectionAttributeDecorationPlugin,
  createSpanWrapperDecorationPlugin,
} from '../decoration-plugins';
import styles from '../statics/styles.scss';

export interface ParagraphOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    paragraph: {
      /**
       * Toggle a paragraph
       */
      setParagraph: () => ReturnType;
    };
  }
}

const createStyleAttribute = (node: ProsemirrorNode) => {
  const attrLineHeight = node.attrs.textStyle?.lineHeight;

  const lineHeight = attrLineHeight ? `line-height: ${attrLineHeight};` : '';
  const style = lineHeight;
  return { style };
};

export const paragraph = createRicosNodeExtension<{
  HTMLAttributes: Record<string, string>;
}>({
  groups: ['shortcuts-enabled', 'no-trailing-node'],
  type: 'html-node',
  name: Node_Type.PARAGRAPH,
  createExtensionConfig() {
    return {
      name: this.name,
      priority: 1000,

      addOptions() {
        return {
          HTMLAttributes: {
            class: styles.paragraph,
          },
        };
      },

      group: 'block',

      content: 'inline*',

      addAttributes() {
        return paragraphDataDefaults;
      },

      parseHTML() {
        return [{ tag: 'p' }, { tag: 'br' }];
      },

      renderHTML({ HTMLAttributes, node }) {
        const styles = createStyleAttribute(node);
        const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styles);
        return ['p', attrs, 0] as DOMOutputSpec;
      },

      addCommands() {
        return {
          setParagraph:
            () =>
            ({ commands }) => {
              return commands.updateTextNode(this.name);
            },
        };
      },

      addKeyboardShortcuts() {
        return {
          // 'Mod-Alt-0': () => this.editor.commands.setParagraph(),
        };
      },

      addProseMirrorPlugins() {
        return [
          createSpanWrapperDecorationPlugin(Node_Type.PARAGRAPH),
          createDirectionAttributeDecorationPlugin(Node_Type.PARAGRAPH),
          new Plugin({
            key: new PluginKey('handlePlainTextPaste'),
            props: {
              handlePaste: (view, event) => {
                const { state } = view;
                const { tr } = state;

                if (event.clipboardData?.types.includes('text/html')) {
                  return false;
                }

                const clipboardText = event.clipboardData?.getData('text/plain').trim();

                if (!clipboardText) {
                  return false;
                }

                const textLines = clipboardText.split(/(?:\r\n|\r|\n)/g);

                const nodes = textLines.map(line => {
                  if (line === '') {
                    return state.schema.nodes.PARAGRAPH.create();
                  } else {
                    const textNode = state.schema.text(line);
                    return state.schema.nodes.PARAGRAPH.create(null, textNode);
                  }
                });

                view.dispatch(
                  tr.replaceSelection(Slice.maxOpen(Fragment.fromArray(nodes))).scrollIntoView()
                );

                return true;
              },
            },
          }),
        ];
      },
    };
  },
});
